import { FC, ReactNode } from "react";
import { Skeleton } from "antd";
import classNames from "classnames";
import { MdCheck, MdOutlineDescription } from "react-icons/md";
import { useTranslation } from "react-i18next";
import ResponsiveSheet from "@Components/ResponsiveSheet/ResponsiveSheet";
import { useHotelDynamicDetails } from "src/Modules/Hotels/Pages/HotelDetails/context";
import RateConditionsTrigger from "./components/RateConditionsTrigger";
import RateConditionsContent from "./components/RateConditionsContent";
import { RateConditionRow } from "./utils";

export type RateConditionsProps = {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
};

export type RateConditionsRenderProps = {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  conditionsList: ReactNode;
  onCheck: (e: { target: { checked: boolean }; stopPropagation: () => void }, close?: () => void) => void;
};

const RateConditions: FC<RateConditionsProps> = ({ checked, onCheckedChange, className }) => {
  const { t } = useTranslation();
  const { rateConditions, isRateConditionsFetching } = useHotelDynamicDetails();
  const hasRateConditions = Boolean(!isRateConditionsFetching && rateConditions?.length);

  const handleCheck = (e: { target: { checked: boolean }; stopPropagation: () => void }, close?: () => void) => {
    e.stopPropagation();
    onCheckedChange?.(e.target.checked);
    if (e.target.checked) close?.();
  };

  const conditionsList = rateConditions?.map((condition, index) => <RateConditionRow key={index} condition={condition} />);

  return (
    <div className={classNames("w-full", className)}>
      <div
        className={classNames(
          "bg-background-default flex w-full flex-col items-start gap-3 rounded-sm p-4 shadow-sm transition-colors md:flex-row md:items-center md:justify-between",
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={classNames(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition-colors",
              checked ? "bg-font-success/15 text-font-success" : "bg-background-minimal text-font-subtle",
            )}
          >
            {checked ? <MdCheck className="h-5 w-5" /> : <MdOutlineDescription className="h-5 w-5" />}
          </div>
          <div className="text-font-default text-base font-semibold">{t("Hotels.guests.acceptTermsPrompt")}</div>
        </div>
        {isRateConditionsFetching ? (
          <Skeleton.Button active shape="round" style={{ width: 170 }} className="self-end md:self-auto" />
        ) : (
          hasRateConditions && (
            <ResponsiveSheet
              label={<div className="text-font-default text-lg font-semibold">{t("Hotels.guests.policiesAndInformation")}</div>}
              modalProps={{ mask: { closable: false } }}
              renderTrigger={({ open }) => <RateConditionsTrigger onOpen={open} />}
              renderSheet={({ close }) => (
                <RateConditionsContent checked={checked} conditionsList={conditionsList} onCheck={handleCheck} onClose={close} />
              )}
            />
          )
        )}
      </div>
    </div>
  );
};

export default RateConditions;
